import {
  Modal,
  Button,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
  useDisclosure,
  Text,
  TextInput,
  Icon,
  Box,
  Checkbox,
  HStack,
  Link,
  Spinner,
} from '@televet/kibble-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Tooltip from 'shared/components/Tooltip';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useFindEmployerIdentificationNumberQuery,
  FindEmployerIdentificationNumberResponse,
  useFindClinicLegalEntityQuery,
  Role,
  useUpsertOneLegalEntityMutation,
  LegalEntityType,
} from 'shared/types/graphql';
import { env } from 'env';

const isProduction = env.REACT_APP_PRODUCTION === 'true';

const formatEmployerIdentificationNumber = (ein: string | undefined): string | undefined => {
  if (!ein) {
    return ein;
  }
  let formattedEin = ein.replace(/[^0-9]/g, '').slice(0, 9);
  formattedEin = ein.replace(/(\d{2})(\d{7})/, '$1-$2');
  return formattedEin;
};

const EmployerIdentificationNumberModal = (): JSX.Element => {
  const { clinicUser, currentClinic, currentClinicId } = useClinicUser();
  const clinicName = currentClinic?.name || '';
  const clinicId = currentClinic?.id || '';
  const isAdmin = useMemo(() => clinicUser?.vetInfo?.roles?.some((r) => r.role === Role.Admin), [clinicUser]);
  const [searchTerm, setSearchTerm] = useState(clinicName);
  const [selectedEIN, setSelectedEIN] = useState<FindEmployerIdentificationNumberResponse | undefined>();
  const [isSoleProprietor, setIsSoleProprietor] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    lookUp: '',
    businessName: '',
    ein: '',
    soleProprietor: '',
  });

  const { isImpersonating } = useAuth();

  const { isOpen: isOpenLookUp, onOpen: onOpenLookUp, onClose: onCloseLookUp } = useDisclosure();
  const { isOpen: isOpenManualInput, onOpen: onOpenManualInput, onClose: onCloseManualInput } = useDisclosure();

  const location = useLocation();

  const {
    data: einData,
    loading: isLoadingEIN,
    refetch,
  } = useFindEmployerIdentificationNumberQuery({
    variables: { data: { companyName: clinicName } },
    skip: !searchTerm || !isOpenLookUp,
  });

  const {
    data: legalEntityData,
    loading: isLegalEntityLoading,
    refetch: refetchLegalEntity,
  } = useFindClinicLegalEntityQuery({
    variables: { where: { id: currentClinicId || '' } },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !isAdmin || !clinicId,
  });

  // This useEffect is used to open the modal if the clinic is missing an EIN
  // only in production
  useEffect(() => {
    if (isProduction && !isImpersonating) {
      const params = new URLSearchParams(location.search);
      const openEinLookup = !!params.get('openEinLookup');
      const openEinManualInput = !!params.get('openEinManualInput');
      const { ein, name } = legalEntityData?.findUniqueClinic?.legalEntity || {};

      if (isAdmin && !isLegalEntityLoading && (!ein || !name)) {
        if (openEinLookup) {
          onOpenLookUp();
        } else if (openEinManualInput) {
          onOpenManualInput();
        } else {
          onOpenManualInput();
        }
      }
    }
  }, [
    clinicId,
    isAdmin,
    isImpersonating,
    isLegalEntityLoading,
    legalEntityData?.findUniqueClinic?.legalEntity,
    location.search,
    onOpenLookUp,
    onOpenManualInput,
  ]);

  const [upsertOneLegalEntity, { loading: isUpsertLegalEntityLoading }] = useUpsertOneLegalEntityMutation();

  useEffect(() => {
    const legalEntity = legalEntityData?.findUniqueClinic?.legalEntity;
    if (legalEntity) {
      const { name, ein, type } = legalEntity;
      setSelectedEIN({ businessName: name, ein: ein });
      if (type === LegalEntityType.SoleProprietorship) {
        setIsSoleProprietor(true);
      }
    }
  }, [legalEntityData?.findUniqueClinic?.legalEntity]);

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = (): void => {
    refetch({ data: { companyName: searchTerm } });
  };

  const validateData = useCallback((name: string, ein: string, isSoleProprietor: boolean): boolean => {
    let result = true;
    const newValidationErrors = { ein: '', businessName: '', lookUp: '', soleProprietor: '' };
    if (!name) {
      result = false;
      newValidationErrors.businessName = 'Business name is required';
    }
    console.log(ein);
    if (!ein && !isSoleProprietor) {
      result = false;
      newValidationErrors.ein = 'EIN is required unless business is sole proprietor';
    }
    setValidationErrors(newValidationErrors);
    return result;
  }, []);

  const handleSubmitEin = async (): Promise<void> => {
    const ein = isSoleProprietor ? 'EIN_REDACTED' : selectedEIN?.ein?.replace(/[^0-9]/g, '') || '';
    const name = selectedEIN?.businessName || selectedEIN?.companyName || '';

    if (validateData(name, ein, isSoleProprietor)) {
      await upsertOneLegalEntity({
        variables: {
          where: { id: legalEntityData?.findUniqueClinic?.legalEntity?.id || '' },
          create: {
            clinics: { connect: [{ id: clinicId }] },
            name,
            ein,
            type: isSoleProprietor ? LegalEntityType.SoleProprietorship : LegalEntityType.PrivateCorporation,
          },
          update: {
            clinics: { connect: [{ id: clinicId }] },
            name,
            ein,
            type: isSoleProprietor ? LegalEntityType.SoleProprietorship : LegalEntityType.PrivateCorporation,
          },
        },
      });
      refetchLegalEntity();
      onCloseLookUp();
      onCloseManualInput();
    }
  };

  const handleSoleProprietorToggle = (): void => {
    setIsSoleProprietor((isSoleProprietor) => !isSoleProprietor);
  };

  const handleBusinessNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSelectedEIN({ ...selectedEIN, businessName: e.target.value });
  };

  const handleEINChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let { value } = e.target;
    value = value.replace(/[^0-9]/g, '').slice(0, 9);
    value.length > 2 && (value = `${value.slice(0, 2)}-${value.slice(2)}`);
    setSelectedEIN({ ...selectedEIN, ein: value });
  };

  return isAdmin ? (
    <Flex className="EmployerIdentificationNumberModal" width="200px">
      <HStack alignItems="baseline" flex="1">
        <Flex flexDirection="column" flex="1">
          <HStack align="center" mb={2} spacing={1}>
            <Text size="sm" fontWeight="bold">
              EIN
            </Text>
            <Tooltip content="Employer Identification Number">
              <Icon name="infoCircle" size="xs" />
            </Tooltip>
          </HStack>
          {isLegalEntityLoading ? (
            <Box margin="auto">
              <Spinner size="sm" />
            </Box>
          ) : (
            <>
              <Box>
                {formatEmployerIdentificationNumber(
                  legalEntityData?.findUniqueClinic?.legalEntity?.ein.replace(/[^0-9]/g, ''),
                ) || '##-#######'}
              </Box>
              <Flex flex="1" justifyContent="space-between" mt={1}>
                <Button variant="secondary" size="xs" onClick={onOpenLookUp} id="look-ein-online-button">
                  Look up online
                </Button>

                <br />
                <Button variant="secondary" size="xs" onClick={onOpenManualInput} id="manual-ein-input-button">
                  Manual Input
                </Button>
              </Flex>
            </>
          )}
        </Flex>
      </HStack>

      <Modal isOpen={isOpenLookUp} onClose={onCloseLookUp} size="2xl">
        <ModalHeader>EIN Lookup</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex>
            <TextInput
              placeholder="Company Name or EIN"
              leftElement={<Icon name="magnifyingGlass" />}
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
            <Button variant="secondary" ml={3} onClick={handleSearch}>
              Search
            </Button>
          </Flex>
          {isLoadingEIN ? (
            <Flex mt={3} minHeight="400px" flex="1" justifyContent="center" alignItems="center">
              <Spinner size="xl" />
            </Flex>
          ) : (
            <Flex flex="1" flexWrap="wrap" minHeight="300px" maxHeight="300px" overflowY="scroll" mt={3}>
              {!einData?.findEmployerIdentificationNumber?.length && (
                <Flex flex="1" justifyContent="center">
                  <Text>Can not find any EIN information</Text>
                </Flex>
              )}
              {einData?.findEmployerIdentificationNumber?.map((ein) => {
                return (
                  <Box
                    key={ein?.ein}
                    padding={3}
                    border="1px"
                    borderColor={ein?.ein === selectedEIN?.ein ? 'border.success' : 'border.default'}
                    backgroundColor={
                      ein?.ein === selectedEIN?.ein ? 'background.success' : 'background.primaryAlpha.hover'
                    }
                    margin={1}
                    borderRadius="8px"
                    _hover={{
                      backgroundColor: ein?.ein === selectedEIN?.ein ? 'background.success' : 'background.alpha.hover',
                      cursor: 'pointer',
                    }}
                    flex="0 0 47%"
                    maxH="240px"
                    onClick={(): void => setSelectedEIN(ein as FindEmployerIdentificationNumberResponse)}
                  >
                    <Box>
                      <Text fontWeight="bold">Name:</Text> {ein?.companyName}
                    </Box>
                    {ein?.businessName && (
                      <Box>
                        <Text fontWeight="bold">Doing Business as:</Text> {ein.businessName}
                      </Box>
                    )}
                    <Box>
                      <Text fontWeight="bold">EIN:</Text>
                      {formatEmployerIdentificationNumber(ein?.ein || '')}
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Phone:</Text> {ein?.companyPhone}
                    </Box>

                    <Box>
                      <Text fontWeight="bold">Address:</Text> {ein?.address}
                    </Box>
                    <Box>
                      <Link href={ein?.url || ''} isExternal>
                        Read more
                      </Link>
                    </Box>
                  </Box>
                );
              })}
            </Flex>
          )}
          <Flex mt={3} alignItems="center" justifyContent="flex-end">
            <Checkbox isChecked={isSoleProprietor} onChange={handleSoleProprietorToggle} mr={3} size="md">
              This business operates as a sole proprietor
            </Checkbox>
          </Flex>
        </ModalBody>
        <ModalFooterButtons onSubmit={handleSubmitEin} isLoading={isUpsertLegalEntityLoading} />
      </Modal>
      <Modal isOpen={isOpenManualInput} onClose={onCloseManualInput} size="2xl">
        <ModalHeader>EIN Manual Input</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={3}>
            <TextInput
              value={selectedEIN?.businessName || ''}
              onChange={handleBusinessNameChange}
              type="text"
              label="Company Name"
              errorText={validationErrors?.businessName}
              isInvalid={!!validationErrors?.businessName}
            />
          </Box>
          <Box mb={3}>
            <TextInput
              value={selectedEIN?.ein || ''}
              onChange={handleEINChange}
              type="text"
              label="EIN (Employer Identification Number)"
              errorText={validationErrors?.ein}
              isInvalid={!!validationErrors?.ein}
            />
          </Box>
          <Flex alignItems="center" justifyContent="flex-end">
            <Checkbox isChecked={isSoleProprietor} onChange={handleSoleProprietorToggle} mr={3} size="md">
              This business operates as a sole proprietor
            </Checkbox>
          </Flex>
        </ModalBody>
        <ModalFooterButtons onSubmit={handleSubmitEin} isLoading={isUpsertLegalEntityLoading} />
      </Modal>
    </Flex>
  ) : (
    <></>
  );
};

export default EmployerIdentificationNumberModal;
